import { useState, useEffect, useContext } from "react";
import axios from "axios";
import { AuthContext } from "../../context/AuthContext";
import { Card, Spin, Select, Table, Tag, Row, Col, Typography } from "antd";
import {
  ClockCircleOutlined,
  DollarOutlined,
  UserOutlined,
  RiseOutlined,
} from "@ant-design/icons";
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid,
  Tooltip, Legend, ResponsiveContainer, Cell,
} from "recharts";

const { Text } = Typography;
const { Option } = Select;

const MONTHS = [
  "January","February","March","April","May","June",
  "July","August","September","October","November","December",
];
const now = new Date();
const CURRENT_MONTH = now.getMonth() + 1;
const CURRENT_YEAR  = now.getFullYear();
const YEARS = Array.from({ length: 5 }, (_, i) => CURRENT_YEAR - 2 + i);

const fmtCurrency = (v) =>
  `₹ ${Number(v || 0).toLocaleString("en-IN", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

export default function OtChart() {
  const { authData, backUrl } = useContext(AuthContext);
  const [data,    setData]    = useState(null);
  const [loading, setLoading] = useState(false);
  const [month,   setMonth]   = useState(CURRENT_MONTH);
  const [year,    setYear]    = useState(CURRENT_YEAR);

  useEffect(() => { fetchData(); }, [month, year]);

  const fetchData = async () => {
    setLoading(true);
    try {
      const res = await axios.get(
        `${backUrl}/api/reports/payroll-details/?month=${month}&year=${year}`,
        { headers: { Authorization: `Bearer ${authData?.access}` } }
      );
      setData(res.data);
    } catch (e) {
      console.error("OT data error", e);
    } finally {
      setLoading(false);
    }
  };

  const employees = data?.employees_with_details || [];
  const totalOT   = Number(data?.total_overtime_payment || 0);
  const totalAdv  = Number(data?.total_advance_deductions || 0);

  /* bar chart — top 10 by OT */
  const chartData = [...employees]
    .filter(e => e.overtime_payment > 0 || e.advance_deduction > 0)
    .sort((a, b) => b.overtime_payment - a.overtime_payment)
    .slice(0, 12)
    .map(e => ({
      name: (e.employee_name || "").split(" ")[0],
      "OT Payment":       Number(e.overtime_payment || 0),
      "Advance Deduction": Number(e.advance_deduction || 0),
    }));

  const columns = [
    {
      title: "#",
      key: "idx",
      width: 44,
      render: (_, __, i) => (
        <span style={{ fontWeight: 700, color: "#94a3b8", fontSize: 12 }}>{i + 1}</span>
      ),
    },
    {
      title: "Code",
      dataIndex: "employee_code",
      width: 80,
      render: v => <Tag style={{ borderRadius: 6, fontWeight: 700, fontSize: 11 }}>{v}</Tag>,
    },
    {
      title: "Employee",
      dataIndex: "employee_name",
      render: (name, rec) => (
        <div>
          <div style={{ fontWeight: 700, color: "#0f172a", fontSize: 13 }}>{name}</div>
          <div style={{ fontSize: 11, color: "#94a3b8" }}>{rec.designation} · {rec.department}</div>
        </div>
      ),
    },
    {
      title: "OT Payment",
      dataIndex: "overtime_payment",
      align: "right",
      render: v => (
        <span style={{ fontWeight: 800, color: "#10b981", fontSize: 13 }}>
          {fmtCurrency(v)}
        </span>
      ),
      sorter: (a, b) => a.overtime_payment - b.overtime_payment,
    },
    {
      title: "Advance Deduction",
      dataIndex: "advance_deduction",
      align: "right",
      render: v => (
        <span style={{ fontWeight: 800, color: "#ef4444", fontSize: 13 }}>
          {fmtCurrency(v)}
        </span>
      ),
      sorter: (a, b) => a.advance_deduction - b.advance_deduction,
    },
  ];

  return (
    <Card
      variant="borderless"
      style={{ borderRadius: 16, border: "1px solid #e2e8f0", boxShadow: "0 2px 16px rgba(15,23,42,0.05)" }}
      styles={{ body: { padding: "18px 20px" } }}
    >
      {/* ── Header ── */}
      <Row justify="space-between" align="middle" style={{ marginBottom: 18 }}>
        <Col>
          <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
            <div style={{
              width: 38, height: 38, borderRadius: 10,
              background: "linear-gradient(135deg,#10b981,#059669)",
              display: "flex", alignItems: "center", justifyContent: "center",
            }}>
              <ClockCircleOutlined style={{ color: "#fff", fontSize: 18 }} />
            </div>
            <div>
              <div style={{ fontWeight: 800, fontSize: 15, color: "#0f172a" }}>
                Overtime & Advance Deductions
              </div>
              <div style={{ fontSize: 12, color: "#64748b" }}>
                Monthly OT payments and advance deductions · {MONTHS[month - 1]} {year}
              </div>
            </div>
          </div>
        </Col>
        <Col>
          <div style={{ display: "flex", gap: 8 }}>
            <Select value={month} onChange={setMonth} style={{ width: 120 }} size="small">
              {MONTHS.map((m, i) => <Option key={i + 1} value={i + 1}>{m}</Option>)}
            </Select>
            <Select value={year} onChange={setYear} style={{ width: 82 }} size="small">
              {YEARS.map(y => <Option key={y} value={y}>{y}</Option>)}
            </Select>
          </div>
        </Col>
      </Row>

      {loading ? (
        <div style={{ textAlign: "center", padding: "60px 0" }}>
          <Spin size="large" />
          <div style={{ marginTop: 12, color: "#64748b", fontWeight: 600 }}>Loading OT & Advance data…</div>
        </div>
      ) : (
        <>
          {/* ── KPI Strip ── */}
          <Row gutter={[12, 12]} style={{ marginBottom: 20 }}>
            <Col xs={24} sm={8}>
              <div style={{
                background: "linear-gradient(135deg,#f0fdf4,#dcfce7)",
                border: "1px solid #bbf7d0", borderRadius: 12, padding: "12px 16px",
              }}>
                <div style={{ fontSize: 11, fontWeight: 700, color: "#15803d", textTransform: "uppercase", marginBottom: 4 }}>
                  Total OT Payment
                </div>
                <div style={{ fontSize: 22, fontWeight: 900, color: "#10b981" }}>
                  {fmtCurrency(totalOT)}
                </div>
              </div>
            </Col>
            <Col xs={24} sm={8}>
              <div style={{
                background: "linear-gradient(135deg,#fff1f2,#fee2e2)",
                border: "1px solid #fecaca", borderRadius: 12, padding: "12px 16px",
              }}>
                <div style={{ fontSize: 11, fontWeight: 700, color: "#b91c1c", textTransform: "uppercase", marginBottom: 4 }}>
                  Total Advance Deductions
                </div>
                <div style={{ fontSize: 22, fontWeight: 900, color: "#ef4444" }}>
                  {fmtCurrency(totalAdv)}
                </div>
              </div>
            </Col>
            <Col xs={24} sm={8}>
              <div style={{
                background: "linear-gradient(135deg,#eff6ff,#dbeafe)",
                border: "1px solid #bfdbfe", borderRadius: 12, padding: "12px 16px",
              }}>
                <div style={{ fontSize: 11, fontWeight: 700, color: "#1d4ed8", textTransform: "uppercase", marginBottom: 4 }}>
                  Employees with OT/Advance
                </div>
                <div style={{ fontSize: 22, fontWeight: 900, color: "#3b82f6" }}>
                  {employees.length}
                </div>
              </div>
            </Col>
          </Row>

          {/* ── Bar Chart ── */}
          {chartData.length > 0 && (
            <div style={{ marginBottom: 20 }}>
              <div style={{ fontSize: 13, fontWeight: 700, color: "#334155", marginBottom: 10 }}>
                Top Employees by OT & Advance
              </div>
              <ResponsiveContainer width="100%" height={220}>
                <BarChart data={chartData} barSize={18} margin={{ top: 0, right: 16, bottom: 0, left: 10 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" />
                  <XAxis dataKey="name" tick={{ fontSize: 11, fill: "#64748b" }} />
                  <YAxis tick={{ fontSize: 11, fill: "#64748b" }} tickFormatter={v => `₹${(v/1000).toFixed(0)}k`} />
                  <Tooltip formatter={(v, n) => [fmtCurrency(v), n]} />
                  <Legend wrapperStyle={{ fontSize: 12 }} />
                  <Bar dataKey="OT Payment" fill="#10b981" radius={[4, 4, 0, 0]} />
                  <Bar dataKey="Advance Deduction" fill="#ef4444" radius={[4, 4, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </div>
          )}

          {/* ── Employee Table ── */}
          <div style={{ fontSize: 13, fontWeight: 700, color: "#334155", marginBottom: 10 }}>
            Employee-wise OT & Advance Details
          </div>
          <Table
            dataSource={employees}
            columns={columns}
            rowKey="id"
            size="small"
            pagination={{ pageSize: 8, showSizeChanger: false }}
            scroll={{ x: 560 }}
            locale={{ emptyText: <div style={{ padding: "32px 0", color: "#94a3b8", textAlign: "center" }}>No OT or Advance records for this period.</div> }}
            style={{ borderRadius: 10, overflow: "hidden", border: "1px solid #e2e8f0" }}
          />
        </>
      )}
    </Card>
  );
}
